import {Type} from '@mintdata/mint-kit/api/types/Type';
import {IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AError} from '@mintdata/mint-kit/api/values/AError';
import {AInteger} from '@mintdata/mint-kit/api/values/AInteger';
import {BaseValue} from '@mintdata/mint-kit/api/values/BaseValue';
import {
    alias,
    custom,
    IDeserializeContext,
    list,
    serializable,
    serialize,
    SKIP
} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import _find from 'lodash/find';

export interface IEnum<T extends IValue> extends IValue
{
    readonly value:T;
    readonly values:Array<T>;
}

const valueModelSchema = custom(
    function(value:AInteger, key:string | number, source:any, baseValue:any)
    {
        return baseValue && baseValue.isEqualTo && baseValue.isEqualTo(value)
            ? SKIP
            : value && value.value != null
                ? value.value
                : serialize(AEmpty.Value);
    },
    function(jsonValue:any,
             context?:IDeserializeContext,
             currentPropertyValue?:any,
             baseValue?:any)
    {
        if( jsonValue == null )
            return AEmpty.Value;

        if( typeof jsonValue != 'number' )
            return AError.fromString('#InvalidIntegerValue!');

        return AInteger.fromNumber(jsonValue);
    }
);

export class AIntegerEnum extends BaseValue implements IEnum<AInteger>
{
    @serializable(alias(
        'selectedValue',
        valueModelSchema
    ))
    public readonly value:AInteger;
    @serializable(list(valueModelSchema))
    public readonly values:Array<AInteger>;

    public constructor(value:AInteger, values:Array<AInteger>)
    {
        if( !BaseValue.isSkipValidation(AIntegerEnum) )
        {
            values = values && values.filter(
                (enumValue:IValue):boolean => Type.isInstanceOf(enumValue, Type.Integer)
            );

            if( !values || values.length === 0 )
                return AError.fromString('#Invalid IntegerEnum values!') as any;

            if( value
                && !Type.isInstanceOf(value, Type.Integer)
                && !Type.isInstanceOf(value, Type.Error) )
                return AError.fromString('#Invalid IntegerEnum value type!') as any;

            if( !AIntegerEnum.isValidValue(value, values) )
                return AError.fromString('#Not allowed IntegerEnum value!') as any;
        }

        super(Type.IntegerEnum);
        this.value = value;
        this.values = values;
    }

    public toString():string
    {
        return this.value != null ? this.value.toString() : null;
    }

    public toValue():string
    {
        return serialize(this);
    }

    public isEqualTo(other:IValue):boolean
    {
        return other
            && Type.isInstanceOf(other, Type.IntegerEnum)
            && ((!this.value && !(other as AIntegerEnum).value)
                || this.value.isEqualTo((other as AIntegerEnum).value));
    }

    public compareTo(other:IValue):number
    {
        if( !other ) return 1;
        if( Type.isInstanceOf(other, Type.Error) || Type.isInstanceOf(other, Type.Empty) )
        {
            return 1;
        }
        if( this.isEqualTo(other) )
            return 0;

        if( Type.isInstanceOf(other, Type.IntegerEnum) )
        {
            return (this.value > (other as AIntegerEnum).value) ? 1 : -1;
        }

        return -1;
    }

    public clone():IValue
    {
        const clonedValues:Array<IValue> = this.values ? this.values.map(value => value.clone()) : null;
        return new AIntegerEnum(this.value && this.value.clone() as AInteger, clonedValues as any);
    }

    public cloneWith(newValue:AInteger):IValue
    {
        const clonedValues:Array<IValue> = this.values ? this.values.map(value => value.clone()) : null;
        return new AIntegerEnum(newValue, clonedValues as any);
    }

    public static isValidValue<T extends IValue>(value:T, values:Array<T>):boolean
    {
        return value == null || !!_find(values, currentValue =>
        {
            return value.isEqualTo(currentValue);
        });
    }

    public static circularRefResolver(value:string):IValue
    {
        // KEEP_ALIVE! Fix for resolving import circular dependencies for AError and ABaseValue
        return new AError('ERROR: Deprecated');
    }
}
